import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, InputGroup, Row, Tab, Tabs } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as OutlineCheck16 } from "assets/svg/outline-check-16.svg";
import { ReactComponent as OutlineCopy16 } from "assets/svg/outline-copy-16.svg";
import ErrorMessage from "components/error-message";
import Loading from "components/Loading";
import ClientCommunication from "./ClientCommunication";
import Reminders from "./Reminders";
import { useTimeOnPageContext } from "contexts/time-on-page";
import { useOrchidPro } from "../../contexts/orchid-pro-context";
import createDomain from "services/client-portal/createDomain";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import styles from "./ClientPortalSetting.module.scss";

const CLIENT_PORTAL_SETTING = [
  { value: "domain", label: "Domain" },
  { value: "client-communication", label: "Client Communication" },
  { value: "reminders", label: "Reminders" },
];

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-client-portal";

const ClientPortalSetting = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { orchidPro } = useOrchidPro();

  const [error, setError] = useState<any>();
  const [validated, setValidated] = useState(false);
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();
  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "domain");

  const [domain, setDomain] = useState("");
  const [urlCreated, setUrlCreated] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (orchidPro?.client_portal_url) {
      setUrlCreated(orchidPro.client_portal_url);
    }
  }, [
    orchidPro?.client_portal_url,
    orchidPro?.send_welcome_email_to_new_patients,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setError(undefined);

    if (!domain) {
      setError("");
      return;
    } else if (domain.length <= 4) {
      setError("Too short: at least 4 characters");
      return;
    } else if (!/^[a-z0-9-]+$/.test(domain)) {
      setError("Only lowercase letters, numbers, and hyphens are allowed");
      return;
    }

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setBusy(true);
    try {
      const res = await createDomain(domain);
      const { orchid_pro_update } = res;
      if (orchid_pro_update.client_portal_url)
        setUrlCreated(orchid_pro_update.client_portal_url);
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!urlCreated) return;
    try {
      await navigator.clipboard.writeText(urlCreated);
      setLinkCopied(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return busy ? (
    <Loading />
  ) : (
    <div className={styles["client-portal-setting"]}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
          className={styles["client-portal-setting__error-message"]}
        />
      )}

      <div className={styles["client-portal-setting__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>Client Portal</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(`/pro-dashboard/settings/client-portal?tab=${k}`, { replace: true });
          }}
          className={styles["client-portal-setting__tabs"]}
        >
          {CLIENT_PORTAL_SETTING.map((el, i) => {
            return (
              <Tab
                key={el.value}
                eventKey={el.value}
                title={el.label}
                tabClassName={styles["client-portal-setting__tab"]}
                onEnter={() =>
                  track(AMPLITUDE_PAGE_NAME, "client-portal-tab", "click")
                }
              ></Tab>
            );
          })}
        </Tabs>
      </div>

      {tab === "domain" && (
        <div className={styles["client-portal-setting__body"]}>
          <div className={styles["client-portal-setting__section"]}>
            <div className={styles["client-portal-setting__section-title"]}>
              Domains
            </div>
            <div className={styles["client-portal-setting__content"]}>
              Your Patient Portal Link is a place where your patients can book
              sessions, and communicate securely with you outside of Orchid
              platform. If you already have an existing website, you can add
              your Patient Portal URL to it. <br />
              <br />
              <b>Note:</b> Please set up at least 1 session type and
              availability before using the Patient Portal link.
            </div>
            <hr className={styles["client-portal-setting__hr"]} />
            <div className={styles["client-portal-setting__your-domain"]}>
              <h4 className={"bd-16"}>Your domain</h4>
              {urlCreated ? (
                <Row className={styles["client-portal-setting__form-row"]}>
                  <div className={styles["client-portal-setting__url-created"]}>
                    {urlCreated}
                  </div>
                  {linkCopied ? (
                    <Button
                      className={styles["client-portal-setting__link-copied"]}
                      onClick={() =>
                        track(AMPLITUDE_PAGE_NAME, "copy-link-button", "click")
                      }
                    >
                      <OutlineCheck16 />
                      <span className={"ml-2"}>Link copied</span>
                    </Button>
                  ) : (
                    <Button
                      className={styles["client-portal-setting__copy-link"]}
                      variant="primary"
                      onClick={() => {
                        track(AMPLITUDE_PAGE_NAME, "copy-link-button", "click");
                        copyLink();
                      }}
                    >
                      <OutlineCopy16 />
                      <span className={"ml-2"}>Copy link</span>
                    </Button>
                  )}
                  <Button
                    disabled={!urlCreated}
                    as={"a"}
                    href={urlCreated}
                    className={"ml-2"}
                    variant="outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track(AMPLITUDE_PAGE_NAME, "view-link-button", "click")
                    }
                  >
                    View link
                  </Button>
                </Row>
              ) : (
                <Form onSubmit={handleSubmit} validated={validated} noValidate>
                  <Row className={styles["client-portal-setting__form-row"]}>
                    <InputGroup
                      className={styles["client-portal-setting__input-group"]}
                    >
                      https://
                      <Form.Control
                        required
                        className={getClasses(
                          error && styles["client-portal-setting__error"],
                          styles["client-portal-setting__form-control"],
                        )}
                        type="text"
                        maxLength={63}
                        minLength={4}
                        value={domain || ""}
                        onChange={(e) => {
                          setError(undefined);
                          setDomain(e.target.value);
                        }}
                      />
                      .orchidportal.health
                      {/* <Form.Control.Feedback type="invalid" tooltip>
                        Too short: at least 4 characters
                      </Form.Control.Feedback> */}
                    </InputGroup>
                    <Button
                      disabled={!domain}
                      variant="primary"
                      type="submit"
                      onClick={() =>
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "save-&-lock-button",
                          "click",
                        )
                      }
                    >
                      Save & Lock
                    </Button>
                    <Button
                      disabled={!urlCreated}
                      as={Link as any}
                      to={"/"}
                      className={"ml-2"}
                      variant="outline-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "preview-link-button",
                          "click",
                        )
                      }
                    >
                      Preview link
                    </Button>
                  </Row>
                  {error && (
                    <div
                      className={styles["client-portal-setting__input-error"]}
                    >
                      {error.toString()}
                    </div>
                  )}
                </Form>
              )}

              <div className={styles["client-portal-setting__note"]}>
                This is your default domain. Once locked, you won't be able to
                change. If you want to change the domain or encounter any
                issues, please contact us at info@orchid.health
              </div>
              <div className={styles["client-portal-setting__requirement"]}>
                Only use:
                <ul>
                  <li>Lower case alphabets</li>
                  <li>Numbers</li>
                  <li>Hypen (-)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "client-communication" && (
        <ClientCommunication amplitudePageName={AMPLITUDE_PAGE_NAME} />
      )}

      {tab === "reminders" && <Reminders />}
    </div>
  );
};

export default ClientPortalSetting;
